import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames/bind'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import styles from './styles/PopUpTopPage.module.css'
import InputSelect from './InputSelect'
import { useThemeContext } from '~/theme/ThemeProvider'
import { hidePopupTopPage } from '~/redux/popupTopPageSlice'
import STORAGE_KEYS from '~/constants/localStorage'
import { fetchDetailRestaurant, selectRestaurant } from '~/redux/restaurantSlice'
import { getListServingTablesApi } from '~/apis/servingTables'
import { EMOJI_REGEX } from '~/constants/regex'
import ButtonConfirm from './ButtonConfirm'
import { KEY_PAGE_POPUP } from '~/constants/popupTopPage'
import { getParentType } from '~/utils/logEventUser'
import { USER_ACTIONS } from '~/constants/logEventUser'
import { logEventUserApi } from '~/apis/logEventUser'
const cx = classNames.bind(styles)

const MAX_LENGTH_NAME = 100

const FormRoomHotel = ({ setKeyPage }) => {
  const { currentThemeObject } = useThemeContext()
  const { restaurantSlug, merchant_type, restaurantId } = useSelector(selectRestaurant)
  const dispatch = useDispatch()
  const [dataFloor, setDataFloor] = useState([])
  const [dataRoomList, setDataRoomList] = useState([])
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  // Các giá trị để gửi lên server
  const [floorSelected, setFloorSelected] = useState(null)
  const [roomSelected, setRoomSelected] = useState(null)
  const [nameUser, setNameUser] = useState('')
  // error của input
  const [errorsInput, setErrorsInput] = useState({
    floor: false,
    room: false,
    name: false,
  })

  // Hàm kiểm tra lỗi form hotel nếu trong trường hợp lỗi sẽ trả về true và hiển thị lỗi bên dưới trường bị lỗi
  const checkErrorForm = () => {
    let isError = false

    if (!nameUser.trim() || !floorSelected?.value || !roomSelected?.value) {
      setErrorsInput({
        ...errorsInput,
        floor: !floorSelected?.value,
        room: !roomSelected?.value,
        name: !nameUser.trim(),
      })

      isError = true
    }

    return isError
  }

  // Hiển thị title popup
  const renderTitle = () => {
    return (
      <Box className={cx('title_form')} color="baseColor.colorText1">
        {t('titles.your_details')}
      </Box>
    )
  }

  const handleChangeNameUser = (e) => {
    const { value, name } = e.target
    if (value.length > MAX_LENGTH_NAME) return

    if (value.match(EMOJI_REGEX)) {
      return
    }
    setNameUser(value)
    setErrorsInput({
      [name]: false,
    })
  }

  const renderInputName = () => {
    return (
      <Box>
        <input
          autoComplete="off"
          type="text"
          placeholder={t('placeholder.your_full_name')}
          value={nameUser}
          name="name"
          className={cx('input')}
          style={{ backgroundColor: currentThemeObject.color13 }}
          onChange={handleChangeNameUser}
        />
        <Box fontSize="1.2rem" color="red">
          {errorsInput.name && t('errors.required_full_name')}
        </Box>
      </Box>
    )
  }

  // Hàm xử lý log action của user khi scan qr code
  const handleLogEventUser = async (tableId) => {
    await logEventUserApi({
      parent_type: getParentType(merchant_type),
      parent_id: restaurantId,
      child_type: null,
      child_id: null,
      locale: currentLanguage,
      position_id: tableId || null,
      action: USER_ACTIONS.scan_qrcode,
    })
  }

  /**
   * Hàm thực hiện lưu table uuid vào redux để phục vụ cho việc order
   * Đóng Popup này
   * Di chuyển lại về trang language
   */
  const handleConfirm = () => {
    if (checkErrorForm()) return

    const tableUid = roomSelected.value
    const tableId = roomSelected.table_id

    handleLogEventUser(tableId)

    dispatch(fetchDetailRestaurant({ restaurantSlug, tableUid })) // call lại api để biết nó có những chức năng gì bên trong bàn
    localStorage.setItem(STORAGE_KEYS.USER_NAME, nameUser.trim()) // Lưu vào local storage để gửi đính kèm trên header
    setKeyPage(KEY_PAGE_POPUP.LANGUAGE)
    dispatch(hidePopupTopPage())
  }

  const renderConfirmBtn = () => {
    return <ButtonConfirm onClick={handleConfirm} label={t('actions.confirm')} />
  }

  /**
   * Hàm xử lý chọn tầng
   * Khi thay đổi tầng thì reset giá trị đã chọn phòng
   */
  const handleChangeFloor = (_e, newValue) => {
    setFloorSelected(newValue)
    setRoomSelected(null)
    setDataRoomList(newValue.serving_tables)
    setErrorsInput({
      ...errorsInput,
      floor: false,
    })
  }

  // Hiển thị input chọn tầng
  const renderInputSelectFloor = () => {
    return (
      <InputSelect
        options={dataFloor}
        onChange={handleChangeFloor}
        itemSelected={floorSelected}
        placeholder={t('placeholder.choose_your_floor')}
        name="floor"
        error={errorsInput.floor && t('errors.required_floor')}
      />
    )
  }

  // Hàm xử lý chọn phòng
  const handleChangeRoom = (_e, newValue) => {
    setRoomSelected(newValue)
    setErrorsInput({
      ...errorsInput,
      room: false,
    })
  }

  // Hiển thị input chọn phòng
  const renderInputSelectRoom = () => {
    return (
      <Box mt="10px">
        <InputSelect
          options={dataRoomList}
          onChange={handleChangeRoom}
          itemSelected={roomSelected}
          placeholder={t('placeholder.choose_your_room')}
          name="room"
          error={errorsInput.room && t('errors.required_room')}
        />
      </Box>
    )
  }

  // Gọi api lấy danh sách tầng và phòng
  const getServingTablesList = useCallback(async () => {
    const { data, isSuccess } = await getListServingTablesApi({ restaurantSlug })
    if (isSuccess) {
      setDataFloor(data)
    }
  }, [restaurantSlug])

  useEffect(() => {
    getServingTablesList()
  }, [getServingTablesList])

  return (
    <Box bgcolor="baseColor.color1">
      {renderTitle()}
      {renderInputSelectFloor()}
      {renderInputSelectRoom()}
      {renderInputName()}
      {renderConfirmBtn()}
    </Box>
  )
}

FormRoomHotel.propTypes = {
  setKeyPage: PropTypes.func,
}

export default React.memo(FormRoomHotel)
